<script lang="ts">
	//Services
	import { format } from '../../../../services/i18n';
	import { gen2Request } from '../../../../services/gen_2_http_reqs';
	//Helpers
	import {
		get_gen_2_component_from_category,
		real_id,
		get_device_component_index,
		get_gen_2_component_channel_structure,
	} from '../../../../helpers/interface_helpers';
	import {
		convertSeconds,
		convertToSeconds,
		formatTwoDigits,
		formatTimeLabel,
	} from '../../../../helpers/timer_helpers';
	//Stores
	import { getDeviceStatus, readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	//Types
	import type { SettingRequest } from '../../../../types/requests/settings/setting';
	import type { Device } from '../../../../types/Device';
	//Design system components
	import { Button, Checkbox, toast, Container, TimeWheel } from '@Shelly/shelly-design-system';
	import { onMount } from 'svelte';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	//Components
	import AwayModeActiveAlert from 'src/components/Alerts/AwayModeActiveAlert.svelte';

	export let device: Device;

	const id = real_id(device.id);
	const component = get_gen_2_component_channel_structure(device);

	let settings = readDeviceSettings(id)[component];

	// Get current status to check away mode
	$: statusStore = getDeviceStatus(id);
	$: status = $statusStore;
	$: awayModeActive = status?.[component]?.away_mode || false;

	let boost_time = settings?.boost_time;

	let loading = false;
	let min = 60,
		max = 86400; //24 hours in seconds

	let selectedHour = { boost_time: '00' };
	let selectedMinute = { boost_time: '00' };
	let selectedSecond = { boost_time: '00' };
	let selectedMs = { boost_time: undefined };
	let maxTimes = convertSeconds(max);

	const method = `${get_gen_2_component_from_category(device)}.SetConfig`;

	$: infoText = `${$format('timer_info_description', {
		values: {
			min: min,
			max: formatTimeLabel(
				maxTimes.hours,
				maxTimes.minutes,
				maxTimes.seconds,
				maxTimes.milliseconds,
			),
		},
	})}`;

	$: errors = {
		boost_time:
			convertToSeconds(
				selectedHour['boost_time'],
				selectedMinute['boost_time'],
				selectedSecond['boost_time'],
				selectedMs['boost_time'],
			) > max
				? infoText
				: '',
	};

	onMount(() => {
		updateTimeValues('boost_time');
	});

	const getTimeInSeconds = (type: 'boost_time') => {
		return convertToSeconds(selectedHour[type], selectedMinute[type], selectedSecond[type]);
	};

	const updateTimeValues = (type: 'boost_time') => {
		const { hours, minutes, seconds, milliseconds } = convertSeconds(boost_time || 0);

		selectedHour[type] = formatTwoDigits(hours);
		selectedMinute[type] = formatTwoDigits(minutes);
		selectedSecond[type] = formatTwoDigits(seconds);
		selectedMs[type] = milliseconds ? formatTwoDigits(milliseconds) : undefined;
	};

	const getTimeWheelTexts = () => {
		return {
			hours: $format('timer_text_hr', { default: 'Hours' }),
			minutes: $format('timer_text_min', { default: 'Minutes' }),
			seconds: $format('timer_text_sec', { default: 'Seconds' }),
		};
	};

	const set_auto = async (setting) => {
		try {
			loading = true;

			const secconds = getTimeInSeconds(`boost_time` as any);

			const params = {
				id: get_device_component_index(device),
				config: {
					boost_time: secconds,
				},
			};

			const res: SettingRequest = await gen2Request(device, method, params);
			const dangerToast = res?.errors?.message ? res?.errors?.message : $format('timer_err_toast');
			const response = onSettingsResponse(res, device, { danger: dangerToast });

			if (!response?.isok) {
				throw new Error('Boost time not set');
			}
			loading = false;
		} catch (e) {
			console.error(e);
			loading = false;
		}
	};
</script>

{#if awayModeActive}
	<AwayModeActiveAlert {device} />
{:else}
	<Container type="modalContainer" padding="0" class="border-color-transparent">
		<TimeWheel
			id="timewheel-oo6o3ga"
			a11yProps={{
				inputAriaLabel: $format('label_time_wheel_input', { default: 'Time wheel input' }),
				buttonAriaLabel: $format('label_time_wheel_button', { default: 'Time wheel button' }),
				secondsColumnAriaLabel: $format('label_time_wheel_seconds_column', {
					default: 'Time wheel seconds column',
				}),
				secondPrefixAriaLabel: $format('label_time_wheel_second_prefix', {
					default: 'Time wheel second prefix',
				}),
				msColumnAriaLabel: $format('label_time_wheel_ms_column', {
					default: 'Time wheel ms column',
				}),
				msPrefixAriaLabel: $format('label_time_wheel_ms_prefix', {
					default: 'Time wheel ms prefix',
				}),
				hoursColumnAriaLabel: $format('label_time_wheel_hours_column', {
					default: 'Time wheel hours column',
				}),
				hourPrefixAriaLabel: $format('label_time_wheel_hour_prefix', {
					default: 'Time wheel hour prefix',
				}),
				minutesColumnAriaLabel: $format('label_time_wheel_minutes_column', {
					default: 'Time wheel minutes column',
				}),
				minutePrefixAriaLabel: $format('label_time_wheel_minute_prefix', {
					default: 'Time wheel minute prefix',
				}),
			}}
			class="mb-s"
			bind:selectedHour={selectedHour[`boost_time`]}
			bind:selectedMinute={selectedMinute[`boost_time`]}
			bind:selectedSecond={selectedSecond[`boost_time`]}
			bind:selectedMs={selectedMs[`boost_time`]}
			enableMilliseconds={false}
			maxHours={maxTimes.hours}
			info={infoText}
			error={errors.boost_time}
			texts={getTimeWheelTexts()}
		/>

		<Button
			{loading}
			disabled={loading || errors.boost_time}
			size="m"
			width="full"
			on:click={() => set_auto('boost_time')}>{$format('button_save')}</Button
		>
	</Container>
{/if}
